import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {SectionView} from '../../ui_canvas_engine/SectionView'
import {TradeViewSpriteButton} from '../TradeViewSpriteButton'
import {FloatingText} from '../../game_ui_view_objects/FloatingText'
import {Sprites} from '../../ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {ArrayUtils} from '@colonist/utils'
import {TradeActions, TradePlayerCreatingCounterOfferStatus, TradeResponse, TradeValidator} from '@colonist/game-mechanic-validators'
import {CardEnum, PlayerColor, TradeResponseStatus} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {GameTypeEnum, ModeSettingEnum} from '@colonist/types'

export abstract class UIGameBaseTradeOfferActionsView extends SectionView {
    abstract onPlayerResponseStatus()
    abstract readonly rejectTradeButton?: TradeViewSpriteButton | undefined

    protected readonly uiGameManager: UIGameManager
    protected readonly stateController: TradeValidator

    onPlayerActionState?: () => void
    onPlayerCreatingCounterOffer?(tradePlayerCreatingCounterOffer: TradePlayerCreatingCounterOfferStatus): void

    protected buttonX: number
    protected actionButtonSize: number

    rejectButtonAction?: () => void

    constructor(uiGameManager: UIGameManager, container: PIXI.Container, x: number, y: number, actionSize: number, stateController: TradeValidator) {
        super(x, y, 0, actionSize, container, true)
        this.uiGameManager = uiGameManager
        this.actionButtonSize = actionSize
        this.stateController = stateController
        this.buttonX = 0
    }

    protected createCounterOfferButton(): TradeViewSpriteButton | undefined {
        const counterOfferAction = this.stateController.getProposeCounterOfferActionStatus(this.uiGameManager.gameController.myColor)
        if(!counterOfferAction.displayActionButton) return undefined

        const isCounterOfferButtonEnabled = counterOfferAction.canTakeAction
        const counterOfferButton = this.createButton(isCounterOfferButtonEnabled, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_pencil))
        const counterOfferButtonAction = () => {
            if(!counterOfferAction.canTakeAction) {
                if(counterOfferAction.errorMessageIfCantTakeAction == undefined) return
                new FloatingText(counterOfferAction.errorMessageIfCantTakeAction, counterOfferButton)
                return
            }

            this.rejectTradeButton?.clearCooldownAnimation()
            const givingResourcesWithoutAnyResourceCards = ArrayUtils.removeAllInstancesOfElementInArray(this.stateController.getGivingResources(this.uiGameManager.gameController.myColor), CardEnum.AnyResource)
            const receivingResourcesWithoutAnyResourceCards = ArrayUtils.removeAllInstancesOfElementInArray(this.stateController.getReceivingResources(this.uiGameManager.gameController.myColor), CardEnum.AnyResource)

            if(this.uiGameManager.gameController.isPlayerSpectator) return
            this.uiGameManager.tradeController.createCounterOffer(this.stateController.state.id, givingResourcesWithoutAnyResourceCards, receivingResourcesWithoutAnyResourceCards)
        }
        counterOfferButton.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [{key: 'strings:game.tooltips.createCounterOffer'}])
        counterOfferButton.updateAction(counterOfferButtonAction)

        return counterOfferButton
    }

    protected createRejectOfferButton(highlightAction?: () => void): TradeViewSpriteButton | undefined {
        const rejectTradeAction = this.stateController.getRejectTradeActionStatus(this.uiGameManager.gameController.myColor)

        if(!rejectTradeAction.displayActionButton) return undefined

        const isRejectButtonEnabled = rejectTradeAction.canTakeAction
        const rejectOfferButton = this.createButton(isRejectButtonEnabled, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_x))
        this.rejectButtonAction = () => this.rejectAction(rejectOfferButton, highlightAction)

        rejectOfferButton.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [{key: 'strings:game.tooltips.rejectTrade'}])
        rejectOfferButton.updateAction(this.rejectButtonAction)

        return rejectOfferButton
    }

    protected createButton(isEnabled: boolean, texture: PIXI.Texture, bgColor?: PlayerColor, iconSize?: number): TradeViewSpriteButton {
        const button = new TradeViewSpriteButton(this.buttonX, 0, this.actionButtonSize, this.actionButtonSize, this, texture, undefined, iconSize, bgColor, this.uiGameManager.gameController.isPlayerSpectator)
        this.buttonX += this.actionButtonSize

        if(!isEnabled) {
            button.inactivateOpacity()
            button.interactive = false
        }

        return button
    }

    protected triggerAutoRejectionCountdown(timeToAutoReject: number) {
        if(this.isAutoTradeRejectionDisabled()) return
        if(!this.rejectTradeButton) return

        const myResponse = this.stateController.getPlayerResponseState(this.uiGameManager.gameController.myColor)
        const isCurrentTurnPlayer = myResponse == undefined
        if(!isCurrentTurnPlayer && myResponse.responseStatus != TradeResponseStatus.PendingResponse) return

        const rejectTradeCallback = () => this.rejectButtonAction?.()

        this.rejectTradeButton.highlight()
        const highlightedButtonOverlay = this.uiGameManager.canvasController.app.renderer.generateTexture(this.rejectTradeButton)
        this.rejectTradeButton.removeHighlight()
        this.rejectTradeButton.startCooldownAnimation(timeToAutoReject, rejectTradeCallback, highlightedButtonOverlay)
    }

    protected amITradeCreator(): boolean {
        return this.stateController.state.creator == this.uiGameManager.gameController.myColor
    }

    private rejectAction(rejectOfferButton: TradeViewSpriteButton, highlightAction?: () => void) {
        const isAcceptForced = this.stateController.state.forcedResponse == TradeResponseStatus.Accepted
        if(isAcceptForced) return this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.BeginnerModeForceOpenGuideBubble)

        const rejectAction = this.stateController.getRejectTradeActionStatus(this.uiGameManager.gameController.myColor)

        if(!rejectAction.canTakeAction) {
            if(rejectAction.errorMessageIfCantTakeAction == undefined) return
            new FloatingText(rejectAction.errorMessageIfCantTakeAction, rejectOfferButton)
            return
        }
        const rejectTrade: TradeResponse = {
            id: this.stateController.state.id,
            response: TradeActions.Reject,
        }
        rejectOfferButton.highlight()
        this.uiGameManager.socketGameSend.updateTradeResponse(rejectTrade)
        rejectOfferButton.clearCooldownAnimation()
        if(highlightAction != undefined) highlightAction()
    }

    private isAutoTradeRejectionDisabled(): boolean {
        const isBeginnerMode = this.uiGameManager.gameController.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        const isTournamentRoom = this.uiGameManager.gameController.gameSettings.gameType == GameTypeEnum.CreatedTournamentRoom
        return isBeginnerMode || isTournamentRoom
    }
}
